import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import useApiEp from "../../hooks/useApiEp";
import PermDataSettingOutlinedIcon from "@mui/icons-material/PermDataSettingOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PageTitle from "../../widgets/PageTitle";
import { toast } from "react-toastify";
import SectionLabel from "../../widgets/SectionLabel";
import { UserContext } from "../../context/UserProvider";
import { useNavigate } from "react-router-dom";
import { getHeader, toastError } from "../../utils/util";
import axios from "axios";
import { API_ENDPOINT } from "../../utils/constants";
import { LoadingButton } from "@mui/lab";

// Reusable CopyButton component (unchanged)
const CopyButton = ({ text, label }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      toast.success(`${label} copied to clipboard!`);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <Tooltip title={copied ? "Copied!" : "Copy"}>
      <IconButton onClick={handleCopy} color={copied ? "success" : "default"}>
        {copied ? (
          <CheckCircleOutlineIcon fontSize="small" />
        ) : (
          <ContentCopyIcon fontSize="small" />
        )}
      </IconButton>
    </Tooltip>
  );
};

const ApiSettings = () => {
  const navigate = useNavigate();
  const [apiToken, setApiToken] = useState("");
  const [apiCode, setApiCode] = useState("");
  const [campaignEp, setCampaignEp] = useState("");
  const [sessionEp, setSessionEp] = useState("");
  const { user } = useContext(UserContext);
  const [generating, setGenerating] = useState(false);
  const [submittingCode, setSubmittingCode] = useState(false);
  const {
    data: apiCred,
    isLoading,
    isError,
    error,
    refetch,
  } = useApiEp(`/user/get-api-cred`);

  useEffect(() => {
    if (apiCred) {
      setApiToken(apiCred.apiToken);
      setApiCode(apiCred.apiCode);
      setCampaignEp(apiCred?.endpoint?.campaign ?? "");
      setSessionEp(apiCred?.endpoint?.session ?? "");
    }
  }, [apiCred]);

  const generateNewToken = async () => {
    try {
      setGenerating(true);
      const response = await axios.patch(
        `${API_ENDPOINT}/user/update-api-token`,
        {},
        getHeader()
      );
      toast.success(response.data.message);
      refetch();
    } catch (error) {
      toastError(error);
    } finally {
      setGenerating(false);
    }
  };

  const saveToken = async () => {
    if (!apiCode) {
      toast.error("Please enter code first!");
      return;
    }
    try {
      setSubmittingCode(true);
      const response = await axios.patch(
        `${API_ENDPOINT}/user/update-api-code`,
        { code: apiCode },
        getHeader()
      );
      toast.success(response.data.message);
      refetch();
    } catch (error) {
      toastError(error);
    } finally {
      setSubmittingCode(false);
    }
  };

  return (
    <Container sx={{ mt: 4, mb: 6 }}>
      <PageTitle mb={3}>Account Credentials</PageTitle>

      {/* Customer ID display */}
      <Box display="flex" justifyContent="center" mb={4}>
        <Chip
          label={user.customerId}
          variant="outlined"
          color="primary"
          sx={{ fontSize: "1rem", px: 2, py: 2.5, fontWeight: 600 }}
        />
      </Box>

      {isLoading && <Typography>Loading credentials...</Typography>}
      {isError && (
        <Typography color="error" mb={2}>
          Error: {error?.message || "Failed to load credentials"}
        </Typography>
      )}

      {/* API Credentials Card */}
      <Card sx={{ mb: 4, p: 2 }}>
        <CardContent>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <PermDataSettingOutlinedIcon /> API Credentials
          </Typography>

          <Grid container rowSpacing={3} columnSpacing={2}>
            {/* API Token row */}
            <Grid item xs={12} sm={8}>
              <Stack direction="row" gap={1} alignItems="center">
                <TextField
                  fullWidth
                  label="API Token"
                  variant="outlined"
                  size="small"
                  value={apiToken}
                  onChange={(e) => setApiToken(e.target.value)}
                  disabled={isLoading}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <Tooltip title="Your API token is valid for 1 year from generation. Generating a new token invalidates the current one.">
                        <InfoOutlinedIcon color="action" fontSize="small" />
                      </Tooltip>
                    ),
                  }}
                />
                <CopyButton text={apiToken} label="API Token" />
              </Stack>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.5, ml: 1 }}
              >
                Token expires 1 year after creation. Generating a new token will
                replace this one.
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <LoadingButton
                startIcon={<PermDataSettingOutlinedIcon />}
                fullWidth
                onClick={generateNewToken}
                variant="contained"
                loading={generating}
                sx={{ height: "40px" }}
              >
                Generate New Token
              </LoadingButton>
            </Grid>

            {/* API Code row */}
            <Grid item xs={12} sm={8}>
              <Stack direction="row" gap={1} alignItems="center">
                <TextField
                  fullWidth
                  label="API Code"
                  variant="outlined"
                  size="small"
                  value={apiCode}
                  onChange={(e) => setApiCode(e.target.value)}
                  disabled={isLoading}
                />
                <CopyButton text={apiCode} label="API Code" />
              </Stack>
            </Grid>
            <Grid item xs={12} sm={4}>
              <LoadingButton
                startIcon={<SaveOutlinedIcon />}
                fullWidth
                onClick={saveToken}
                variant="contained"
                loading={submittingCode}
                sx={{ height: "40px" }}
              >
                Save Code
              </LoadingButton>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* API Endpoints Card */}
      <Card sx={{ p: 2 }}>
        <CardContent>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <InfoOutlinedIcon /> API Endpoints
          </Typography>

          <Grid container rowSpacing={3} columnSpacing={2}>
            <Grid item xs={12} sm={8}>
              <Stack direction="row" gap={1} alignItems="center">
                <TextField
                  fullWidth
                  label="Campaign Endpoint"
                  variant="outlined"
                  size="small"
                  value={campaignEp}
                  onChange={(e) => setCampaignEp(e.target.value)}
                  disabled={isLoading}
                />
                <CopyButton text={campaignEp} label="Campaign Endpoint" />
              </Stack>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                onClick={() => navigate(`/home/campaign-api-docs`)}
                variant="outlined"
                fullWidth
              >
                Documentation
              </Button>
            </Grid>

            <Grid item xs={12} sm={8}>
              <Stack direction="row" gap={1} alignItems="center">
                <TextField
                  fullWidth
                  label="Session Endpoint"
                  variant="outlined"
                  size="small"
                  value={sessionEp}
                  onChange={(e) => setSessionEp(e.target.value)}
                  disabled={isLoading}
                />
                <CopyButton text={sessionEp} label="Session Endpoint" />
              </Stack>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                onClick={() => navigate(`/home/session-api-docs`)}
                variant="outlined"
                fullWidth
              >
                Documentation
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default ApiSettings;
